import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, apiPost, apiGet, setAccessToken, setOnAuthFailure } from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [accessToken, setAccessTokenState] = useState(null);
    const [bootstrapping, setBootstrapping] = useState(true);

    const applyToken = useCallback((token) => {
        setAccessTokenState(token);
        setAccessToken(token);
    }, []);

    const login = useCallback(
        async (email, password) => {
            const data = await apiPost("/auth/login", { email, password });
            applyToken(data.access_token);
            setUser(data.user);
            try {
                sessionStorage.setItem("nx_user", JSON.stringify(data.user));
            } catch (e) {
                /* ignore */
            }
            return data.user;
        },
        [applyToken],
    );

    const logout = useCallback(async () => {
        try {
            await api.post("/auth/logout");
        } catch (e) {
            /* ignore */
        }
        applyToken(null);
        setUser(null);
        try {
            sessionStorage.removeItem("nx_user");
        } catch (e) {
            /* ignore */
        }
    }, [applyToken]);

    const refreshSession = useCallback(async () => {
        try {
            const resp = await api.post("/auth/refresh");
            const newToken = resp.data?.data?.access_token;
            const u = resp.data?.data?.user;
            if (newToken && u) {
                applyToken(newToken);
                setUser(u);
                return true;
            }
        } catch (e) {
            /* not authenticated */
        }
        return false;
    }, [applyToken]);

    const updateProfile = useCallback(async (payload) => {
        const updated = await import("@/lib/api").then((m) =>
            m.apiPatch("/auth/me", payload),
        );
        setUser(updated);
        try {
            sessionStorage.setItem("nx_user", JSON.stringify(updated));
        } catch (e) {
            /* ignore */
        }
        return updated;
    }, []);

    // Bootstrap: try refresh on first mount; restore user from sessionStorage if present
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const cached = sessionStorage.getItem("nx_user");
                if (cached) setUser(JSON.parse(cached));
            } catch (e) {
                /* ignore */
            }
            const ok = await refreshSession();
            if (!ok && !cancelled) {
                setUser(null);
                applyToken(null);
                try {
                    sessionStorage.removeItem("nx_user");
                } catch (e) {
                    /* ignore */
                }
            }
            if (!cancelled) setBootstrapping(false);
        })();
        return () => {
            cancelled = true;
        };
    }, [refreshSession, applyToken]);

    // On hard 401 failure from interceptor, clear local state
    useEffect(() => {
        setOnAuthFailure(() => {
            setAccessTokenState(null);
            setUser(null);
            try {
                sessionStorage.removeItem("nx_user");
            } catch (e) {
                /* ignore */
            }
        });
    }, []);

    return (
        <AuthContext.Provider
            value={{
                user,
                accessToken,
                bootstrapping,
                isAuthenticated: !!user && !!accessToken,
                login,
                logout,
                updateProfile,
                refreshSession,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used within AuthProvider");
    return ctx;
};
